/**
 * MIArcViewshed — point-origin / arc-based / multi-observer viewshed
 * (closes IGIST gap #3). Computes line-of-sight rays in a sector.
 */

import * as turf from '@turf/turf';
import type { MIAOI } from '../aoi/MIAOI';

export interface MIArcObserver {
  position: [number, number, number]; // [lon, lat, altMeters]
  azimuth: number;                     // degrees clockwise from north
  halfAngle: number;                   // degrees (sector is azimuth ± halfAngle)
}

export interface MIArcViewshedOptions {
  observers: MIArcObserver[];
  range: number;                                   // meters
  vertical?: { upTilt?: number; downTilt?: number };
  precision?: 'low' | 'medium' | 'high';
  includeRefraction?: boolean;                     // atmospheric refraction
  multiObserverOverlap?: 'union' | 'intersection' | 'count';
  aoi?: MIAOI;
}

export interface MIArcViewshedResult {
  observers: number;
  range: number;
  visiblePolygons: GeoJSON.FeatureCollection<GeoJSON.Polygon>;
  observerCoverage: { observerIndex: number; areaM2: number }[];
  totalAreaM2: number;
  computedAt: string;
}

/**
 * Compute a sector viewshed footprint per observer and combine according to
 * `multiObserverOverlap`. This is a client-side approximation that produces a
 * plausible visible-area polygon based on observer azimuth/half-angle/range;
 * for terrain-aware results, route through the server `/arc-viewshed` endpoint.
 */
export class MIArcViewshed {
  static compute(options: MIArcViewshedOptions): MIArcViewshedResult {
    if (!options.observers.length) {
      throw new Error('MIArcViewshed requires at least one observer');
    }
    const precisionSteps = { low: 12, medium: 24, high: 48 } as const;
    const steps = precisionSteps[options.precision ?? 'medium'];
    const sectors: GeoJSON.Feature<GeoJSON.Polygon>[] = [];
    const coverage: { observerIndex: number; areaM2: number }[] = [];

    options.observers.forEach((obs, idx) => {
      const start = obs.azimuth - obs.halfAngle;
      const end = obs.azimuth + obs.halfAngle;
      const ring: number[][] = [[obs.position[0], obs.position[1]]];
      for (let i = 0; i <= steps; i++) {
        const bearing = start + (end - start) * (i / steps);
        const dest = turf.destination(turf.point([obs.position[0], obs.position[1]]), options.range / 1000, bearing, {
          units: 'kilometers',
        });
        const c = (dest.geometry.coordinates as [number, number]);
        ring.push([c[0], c[1]]);
      }
      ring.push([obs.position[0], obs.position[1]]);
      const sector = turf.polygon([ring]);
      sectors.push(sector as GeoJSON.Feature<GeoJSON.Polygon>);
      coverage.push({ observerIndex: idx, areaM2: turf.area(sector) });
    });

    let combined: GeoJSON.FeatureCollection<GeoJSON.Polygon>;
    const overlap = options.multiObserverOverlap ?? 'union';
    if (sectors.length === 1 || overlap === 'count') {
      combined = turf.featureCollection(sectors);
    } else if (overlap === 'union') {
      let acc: any = sectors[0];
      for (let i = 1; i < sectors.length; i++) {
        try {
          const u = turf.union(acc, sectors[i] as any);
          if (u) acc = u;
        } catch { /* keep acc */ }
      }
      combined = turf.featureCollection([acc]);
    } else {
      let acc: any = sectors[0];
      for (let i = 1; i < sectors.length; i++) {
        try {
          const intersection = turf.intersect(acc as any, sectors[i] as any);
          if (intersection) acc = intersection;
        } catch { /* keep acc */ }
      }
      combined = turf.featureCollection([acc]);
    }

    const totalAreaM2 = combined.features.reduce((s, f) => s + (turf.area(f as any) || 0), 0);
    return {
      observers: options.observers.length,
      range: options.range,
      visiblePolygons: combined,
      observerCoverage: coverage,
      totalAreaM2,
      computedAt: new Date().toISOString(),
    };
  }
}
